import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WalletManager from '../components/holder/WalletManager';
import HolderNotificationPrefs, { HOLDER_NOTIF_DEFAULTS } from '../components/holder/HolderNotificationPrefs';
import { User, Wallet, Bell, Save, Check, CreditCard, Gift } from 'lucide-react';
import { toast } from 'sonner';
import ResetTourButton from '../components/ResetTourButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BuyerSettingsPage = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Controlled form state
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [walletInput, setWalletInput] = useState('');
  const [walletSaving, setWalletSaving] = useState(false);
  const [walletSaved, setWalletSaved] = useState(false);
  const [savedPrimaryWallet, setSavedPrimaryWallet] = useState('');
  const [walletList, setWalletList] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState({ email: true, sms: false, push: true });
  const [payoutPrefs, setPayoutPrefs] = useState({ method: 'ACH', currency: 'USD' });
  const [holderPrefs, setHolderPrefs] = useState(HOLDER_NOTIF_DEFAULTS);
  const [delivery, setDelivery] = useState(null);
  const [payoutEligible, setPayoutEligible] = useState(false);

  // RAP opt-out
  const [rapOptOut, setRapOptOut] = useState(false);
  const [rapSaving, setRapSaving] = useState(false);

  useEffect(() => { fetchSettings(); fetchRapPrefs(); }, []);

  const fetchRapPrefs = async () => {
    try {
      const res = await axios.get(`${API}/buyer/preferences`);
      setRapOptOut(!!res.data?.preferences?.rap_opt_out);
    } catch (e) {
      // Not blocking — fallback to false.
    }
  };

  const toggleRapOptOut = async (next) => {
    setRapSaving(true);
    setRapOptOut(next);
    try {
      const res = await axios.put(`${API}/buyer/preferences`, { rap_opt_out: next });
      setRapOptOut(!!res.data?.preferences?.rap_opt_out);
      toast.success(next ? 'Opted out of Kickback Gifts' : 'Opted back into Kickback Gifts');
    } catch (e) {
      setRapOptOut(!next);
      toast.error('Failed to update preference');
    } finally {
      setRapSaving(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/buyer/settings`);
      const d = res.data;
      setDisplayName(d.profile?.display_name || '');
      setEmail(d.profile?.email || '');
      setWalletInput(d.primary_wallet || '');
      setSavedPrimaryWallet(d.primary_wallet || '');
      setWalletList(d.wallets || []);
      setNotifPrefs(d.notification_prefs || { email: true, sms: false, push: true });
      setPayoutPrefs(d.payout_prefs || { method: 'ACH', currency: 'USD' });
      setHolderPrefs(d.holder_notification_prefs || HOLDER_NOTIF_DEFAULTS);
      setDelivery(d.notification_delivery || null);
      setPayoutEligible(!!d.payout_eligible);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWallet = async () => {
    if (!walletInput.trim()) return;
    setWalletSaving(true);
    setWalletSaved(false);
    try {
      const res = await axios.patch(`${API}/buyer/settings/wallets`, {
        primary_wallet: walletInput.trim()
      });
      if (res.data.success) {
        toast.success('Wallet saved!');
        setWalletSaved(true);
        setTimeout(() => setWalletSaved(false), 3000);
        fetchSettings();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save wallet');
    } finally {
      setWalletSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const payload = {
        name: displayName,
        notification_prefs: notifPrefs,
        holder_notification_prefs: holderPrefs,
      };
      if (payoutEligible) payload.payout_prefs = payoutPrefs;
      const res = await axios.put(`${API}/buyer/settings`, payload);
      if (res.data.success) {
        toast.success('Settings saved!');
        fetchSettings();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#0B0B0B' }}>Loading settings...</div>;
  }

  const inputStyle = {
    width: '100%', padding: '12px', background: '#FFFFFF',
    border: '1px solid #E6E8EF', borderRadius: '8px', color: '#0B0B0B', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  const selectStyle = {
    ...inputStyle, appearance: 'none', cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
  };

  const toggleNotif = (key) => setNotifPrefs(p => ({ ...p, [key]: !p[key] }));

  return (
    <div data-testid="buyer-settings-page" style={{ padding: '40px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>Settings</h1>
      <p style={{ fontSize: '16px', color: '#999', marginBottom: '32px' }}>Manage your account preferences</p>

      <div style={{ display: 'grid', gap: '24px', maxWidth: '800px' }}>
        {/* Profile Section */}
        <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E6E8EF' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <User size={20} color="#4B9DFF" />
            <h3 style={{ color: '#0B0B0B', fontSize: '18px', fontWeight: 600 }}>Profile</h3>
          </div>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Display Name</label>
              <input
                data-testid="settings-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={80}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Email</label>
              <input data-testid="settings-email" type="email" value={email} disabled style={{ ...inputStyle, color: '#666' }} />
            </div>
          </div>
        </div>

        <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E6E8EF' }}>
          <WalletManager />
        </div>

        <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E6E8EF' }}>
          <HolderNotificationPrefs value={holderPrefs} onChange={setHolderPrefs} delivery={delivery} />
        </div>

        {/* Payout Preferences — only when a held Kickback has payout activity */}
        {!payoutEligible && (
          <div data-testid="payout-not-applicable" style={{ background: '#FFFFFF', borderRadius: '12px', padding: '16px 24px', border: '1px solid #E6E8EF', fontSize: 13, color: '#5B6472' }}>
            <b>Payouts:</b> none of your Kickbacks currently offer payouts, so there's nothing to set up. No bank or payout account is needed for claims or benefits.
          </div>
        )}
        {payoutEligible && (
        <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E6E8EF' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <CreditCard size={20} color="#4B9DFF" />
            <h3 style={{ color: '#0B0B0B', fontSize: '18px', fontWeight: 600 }}>Payout Preferences</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Payout Method</label>
              <select
                data-testid="payout-method-select"
                value={payoutPrefs.method}
                onChange={(e) => setPayoutPrefs(p => ({ ...p, method: e.target.value }))}
                style={selectStyle}
              >
                <option value="ACH">ACH Bank Transfer</option>
                <option value="PAYPAL">PayPal</option>
                <option value="CRYPTO">Crypto Wallet</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: '#999', fontSize: '13px', marginBottom: '6px' }}>Currency</label>
              <select
                data-testid="payout-currency-select"
                value={payoutPrefs.currency}
                onChange={(e) => setPayoutPrefs(p => ({ ...p, currency: e.target.value }))}
                style={selectStyle}
              >
                <option value="USD">USD</option>
                <option value="USDC">USDC</option>
                <option value="ETH">ETH</option>
              </select>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#5B6472', marginTop: 10 }}>This saves your preference only. Payout delivery depends on the Kickback's payout setup.</div>
        </div>
        )}

        {/* RAP — Kickback Gifts Opt-Out */}
        <div data-testid="rap-opt-out-card" style={{
          background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12,
          padding: 24, marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Gift size={18} color="#6C5CE7" />
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Kickback Gifts</h2>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 4 }}>
                Opt out of Kickback Gifts
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
                You will still keep NFT perks/utilities. You just won&apos;t receive monetary gifts.
                Your share of the kickback pool will be redistributed to remaining eligible holders.
              </div>
            </div>
            <label data-testid="rap-opt-out-toggle" style={{
              position: 'relative', display: 'inline-block', width: 48, height: 26, cursor: rapSaving ? 'not-allowed' : 'pointer',
            }}>
              <input
                data-testid="rap-opt-out-input"
                type="checkbox"
                checked={rapOptOut}
                disabled={rapSaving}
                onChange={(e) => toggleRapOptOut(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', inset: 0, borderRadius: 999,
                background: rapOptOut ? '#6C5CE7' : '#D1D5DB',
                transition: 'background 150ms ease',
              }} />
              <span style={{
                position: 'absolute', top: 3, left: rapOptOut ? 24 : 3,
                width: 20, height: 20, borderRadius: '50%',
                background: '#FFF', transition: 'left 150ms ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </label>
          </div>
        </div>

        {/* Save All Button */}
        <button
          data-testid="save-all-settings-btn"
          onClick={handleSaveAll}
          disabled={saving}
          style={{
            padding: '14px 28px',
            background: saving ? '#666' : '#4B9DFF',
            color: '#FFF',
            border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer', width: 'fit-content',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <ResetTourButton user={user} />
      </div>
    </div>
  );
};

export default BuyerSettingsPage;
